import { useParams } from 'react-router';
import { isEmpty, omitBy, pickBy } from 'remeda';
import { useLoadTemplateV2 } from 'shared/hooks/use-load-template-v2';
import { useUpdateTemplate } from '../api';
import type { TemplateChannel } from './channels';
import { draftKey, useSendingOptionsDraftsAtom, useTemplateDraftsAtom } from './state';
import { type BlankField, useBlankFields } from './use-blank-fields';

export type TemplateSave = {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  blankFields: BlankField[];
  save: () => void;
};

export const useSaveTemplate = (): TemplateSave => {
  const { templateId } = useParams<{ templateId: string }>();
  const { template } = useLoadTemplateV2(templateId);
  const [drafts, setDrafts] = useTemplateDraftsAtom();
  const [sendingOptionsDrafts, setSendingOptionsDrafts] = useSendingOptionsDraftsAtom();
  const { updateTemplate, isUpdateTemplateLoading } = useUpdateTemplate();
  const blankFields = useBlankFields();

  const draftPrefix = template ? `${template.id}:` : null;
  const templateDrafts = draftPrefix
    ? pickBy(drafts, (_body, key) => key.startsWith(draftPrefix))
    : {};
  const templateSendingOptionsDrafts = draftPrefix
    ? pickBy(sendingOptionsDrafts, (_options, key) => key.startsWith(draftPrefix))
    : {};
  const hasUnsavedChanges = !isEmpty(templateDrafts) || !isEmpty(templateSendingOptionsDrafts);

  const save = () => {
    if (!template || !hasUnsavedChanges || isUpdateTemplateLoading) return;
    if (!isEmpty(blankFields)) return;

    const buildBodies = (channel: TemplateChannel) =>
      template.bodies[channel].map(body => {
        const sendingOptions =
          templateSendingOptionsDrafts[draftKey(template.id, channel, body.locale)];
        return {
          id: body.id,
          groupedType: body.groupedType,
          locale: body.locale,
          body: templateDrafts[draftKey(template.id, channel, body.locale)] ?? body.body,
          subject: sendingOptions?.subject ?? body.subject,
          fromName: sendingOptions?.fromName ?? body.fromName,
          fromEmail: sendingOptions?.fromEmail ?? body.fromEmail,
          replyToName: sendingOptions?.replyToName ?? body.replyToName,
          replyToEmail: sendingOptions?.replyToEmail ?? body.replyToEmail,
        };
      });

    updateTemplate(
      { id: template.id, bodies: { email: buildBodies('email'), letter: buildBodies('letter') } },
      {
        // Only clear the drafts we actually saved; edits made mid-save keep their draft.
        onSuccess: () => {
          setDrafts(previous => omitBy(previous, (body, key) => templateDrafts[key] === body));
          setSendingOptionsDrafts(previous =>
            omitBy(previous, (options, key) => templateSendingOptionsDrafts[key] === options),
          );
        },
      },
    );
  };

  return { hasUnsavedChanges, isSaving: isUpdateTemplateLoading, blankFields, save };
};
